import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import * as cognitoService from '../lib/auth/cognito.service';
import { getMyUser, getMyTenants } from '../lib/api/user.client';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { useTokenRefreshOnFocus } from '../hooks/useTokenRefreshOnFocus';
import { useAuthStore } from '../stores/auth.store';
/**
 * ProtectedRoute component that:
 * 1. Checks if user is authenticated
 * 2. Attempts to restore session from Cognito on mount
 * 3. Redirects to /login if not authenticated
 * 4. Shows loading spinner during session validation
 * 5. Tracks inactivity and logs out after 30 minutes of no activity
 */
export default function ProtectedRoute() {
    const location = useLocation();
    const { isAuthenticated, setUser, setTokens, setLoading, setError } = useAuthStore();
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    // Track user inactivity - logs out after 30 minutes of no activity
    // This only applies to portal users, not kiosk devices (they use separate routes)
    // Hook is safe to call unconditionally - it checks isAuthenticated internally
    useInactivityTimeout();
    // When the tab becomes visible after being hidden for >1min, proactively
    // refresh the Cognito session so focus-triggered query refetches don't
    // fire with an expired idToken.
    useTokenRefreshOnFocus();
    // Try to restore session from Cognito on mount
    useEffect(() => {
        const checkSession = async () => {
            try {
                setLoading(true);
                // Check if there's an existing Cognito session
                const session = await cognitoService.getCurrentSession();
                if (session && session.isValid()) {
                    // Session exists, refresh to get latest tokens
                    const result = await cognitoService.refreshSession();
                    // Store tokens first (needed for API calls)
                    setTokens(result.tokens);
                    // Fetch fresh user profile from DynamoDB (authoritative roles)
                    try {
                        const userProfile = await getMyUser();
                        // Fetch tenant info to get company name
                        let companyName;
                        try {
                            const selectedTenantId = localStorage.getItem('selectedTenantId');
                            const { tenants } = await getMyTenants();
                            const currentTenant = tenants.find(t => t.tenantId === (selectedTenantId || result.user.tenantId));
                            companyName = currentTenant?.companyName;
                        }
                        catch (tenantErr) {
                            console.warn('Failed to fetch tenant info:', tenantErr);
                        }
                        // Merge Cognito user data with DynamoDB roles and company name
                        setUser({
                            ...result.user,
                            roles: userProfile.roles, // Use DynamoDB roles, not token roles
                            ...(companyName && { companyName }), // Add company name if available
                            ...(userProfile.employeeId && { employeeId: userProfile.employeeId }),
                        });
                    }
                    catch (profileErr) {
                        console.error('Failed to fetch user profile on session refresh, using token data:', profileErr);
                        // Fallback to token data if API call fails
                        setUser(result.user);
                    }
                }
            }
            catch (err) {
                // No valid session, user needs to log in
                console.log('No valid session found, redirecting to login');
                setError(null); // Clear any previous errors
            }
            finally {
                setLoading(false);
                setIsCheckingSession(false);
            }
        };
        checkSession();
    }, [setUser, setTokens, setLoading, setError]);
    // Show loading spinner while checking session
    if (isCheckingSession) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50", children: _jsxs("div", { className: "text-center", children: [_jsx(Loader2, { className: "h-8 w-8 animate-spin text-blue-600 mx-auto" }), _jsx("p", { className: "mt-4 text-sm text-gray-600", children: "Verifying authentication..." })] }) }));
    }
    // If not authenticated, redirect to login page
    // Preserve the attempted location so we can redirect back after login
    if (!isAuthenticated) {
        return _jsx(Navigate, { to: "/login", state: { from: location }, replace: true });
    }
    // User is authenticated, render protected content
    return _jsx(Outlet, {});
}
